import React, { useMemo, useState } from 'react';
import { X, Plus, Combine, AlertCircle, MapPin } from 'lucide-react';
import { useHolisticStore } from '../../stores/useHolisticStore';
import { useGraphStore } from '../../stores/useGraphStore';
import {
    filterHolisticCanvasCandidates,
    holisticAddressInList,
    listHolisticCanvasCandidates,
} from '../../utils/holisticCanvasCandidates';

interface HolisticTraceModalProps {
    onConfirm: (chain: string, addresses: string[]) => void;
}

/**
 * Outer gate — single hook call so early-return is safe under rules-of-hooks.
 * The inner component only mounts when the modal is actually open, which
 * means useState initializes fresh from the store every time it opens.
 */
export const HolisticTraceModal: React.FC<HolisticTraceModalProps> = (props) => {
    const modalOpen = useHolisticStore(s => s.modalOpen);
    if (!modalOpen) return null;
    return <HolisticTraceModalInner {...props} />;
};

const HolisticTraceModalInner: React.FC<HolisticTraceModalProps> = ({ onConfirm }) => {
    const { modalChain, modalAddresses, closeModal } = useHolisticStore();
    const elements = useGraphStore(s => s.elements);
    const [addresses, setAddresses] = useState<string[]>(modalAddresses);
    const [addInput, setAddInput] = useState('');

    const canvasCandidates = useMemo(
        () => listHolisticCanvasCandidates(elements, modalChain, addresses),
        [elements, modalChain, addresses],
    );
    const visibleCandidates = useMemo(
        () => filterHolisticCanvasCandidates(canvasCandidates, addInput),
        [canvasCandidates, addInput],
    );

    const removeAddress = (idx: number) =>
        setAddresses(prev => prev.filter((_, i) => i !== idx));

    const appendAddress = (raw: string) => {
        const trimmed = raw.trim();
        if (!trimmed || holisticAddressInList(trimmed, addresses)) return;
        setAddresses(prev => [...prev, trimmed]);
        setAddInput('');
    };

    const addAddress = () => appendAddress(addInput);

    const handleConfirm = () => {
        if (addresses.length < 2) return;
        closeModal();
        onConfirm(modalChain, addresses);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
            <div className="bg-card border border-border rounded-xl shadow-overlay w-full max-w-md mx-4 flex flex-col max-h-[80vh]">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
                    <div className="flex items-center gap-2">
                        <Combine className="w-4 h-4 text-primary" />
                        <span className="font-semibold text-sm">Holistic Multi-Node Trace</span>
                    </div>
                    <button onClick={closeModal} className="p-1 rounded hover:bg-secondary transition-colors">
                        <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex flex-col gap-3 p-4 overflow-y-auto flex-1">
                    <p className="text-xs text-muted-foreground">
                        Runs parallel shallow traces across all addresses, identifies cross-links, shared cash-out entities, and generates an AI narrative.
                    </p>

                    <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Chain:</span>
                        <span className="font-mono font-semibold px-2 py-0.5 bg-primary/10 text-primary rounded">
                            {modalChain}
                        </span>
                    </div>

                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Addresses ({addresses.length})
                    </div>

                    <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto pr-1">
                        {addresses.map((addr, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-secondary/40 border border-border/50">
                                <span className="font-mono text-xs flex-1 truncate">{addr}</span>
                                <button
                                    onClick={() => removeAddress(i)}
                                    className="text-muted-foreground hover:text-red-500 transition-colors shrink-0"
                                    title="Remove"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        ))}
                        {addresses.length === 0 && (
                            <p className="text-xs text-muted-foreground italic text-center py-2">No addresses</p>
                        )}
                    </div>

                    {/* Add address */}
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={addInput}
                                onChange={e => setAddInput(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addAddress()}
                                placeholder="Paste another address to add..."
                                className="flex-1 px-3 py-1.5 text-xs bg-background border border-input rounded-md focus:ring-1 focus:ring-primary focus:border-transparent"
                            />
                            <button
                                onClick={addAddress}
                                disabled={!addInput.trim()}
                                className="px-3 py-1.5 bg-secondary hover:bg-secondary/80 rounded-md transition-colors disabled:opacity-50"
                                title="Add"
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {canvasCandidates.length > 0 && (
                            <div className="rounded-md border border-border/60 bg-muted/20 p-2.5">
                                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                                    <MapPin className="w-3 h-3 shrink-0 text-primary" />
                                    On canvas — click to add
                                </div>
                                <p className="text-[11px] text-muted-foreground mb-2">
                                    These wallets are already visible on your Workbench. Select one instead of copy-pasting.
                                </p>
                                <div className="flex flex-col gap-1 max-h-36 overflow-y-auto">
                                    {visibleCandidates.length === 0 ? (
                                        <p className="text-[11px] text-muted-foreground italic py-1">
                                            No canvas matches for &ldquo;{addInput.trim()}&rdquo;.
                                        </p>
                                    ) : visibleCandidates.map((candidate) => (
                                        <button
                                            key={candidate.address}
                                            type="button"
                                            onClick={() => appendAddress(candidate.address)}
                                            className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-md border border-transparent hover:border-primary/30 hover:bg-primary/5 transition-colors"
                                        >
                                            <span className="text-xs font-medium text-foreground truncate min-w-0 flex-1">
                                                {candidate.label}
                                            </span>
                                            <span className="font-mono text-[10px] text-muted-foreground truncate max-w-[42%]">
                                                {candidate.address}
                                            </span>
                                            <Plus className="w-3 h-3 shrink-0 text-primary" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {addresses.length < 2 && (
                        <div className="flex items-center gap-2 text-xs text-amber-500">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            At least 2 addresses required.
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 p-4 border-t border-border shrink-0">
                    <button
                        onClick={closeModal}
                        className="px-4 py-2 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={addresses.length < 2}
                        className="px-4 py-2 text-xs font-medium bg-primary hover:bg-brand-gold-hover text-primary-foreground rounded-md transition-colors disabled:opacity-50"
                    >
                        Start Trace ({addresses.length})
                    </button>
                </div>
            </div>
        </div>
    );
};
